import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, AlertTriangle, Database, FlaskConical, FolderTree, Settings, Book } from 'lucide-react';
import ChemicalInformation from './components/ChemicalInformation';
import ProcessFlowDiagrams from './components/ProcessFlowDiagrams';
import PIDsEquipmentHierarchy from './components/PIDsEquipmentHierarchy';
import DesignBasisDocuments from './components/DesignBasisDocuments';
import OperatingEnvelopes from './components/OperatingEnvelopes';
import EquipmentDataSheets from './components/EquipmentDataSheets';
import ProcessDescription from './components/ProcessDescription';
import Icon from '../../components/AppIcon';


const ProcessSafetyInformationRepository = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('chemical-info');
  const [searchQuery, setSearchQuery] = useState('');

  const sections = [
    { id: 'chemical-info', name: 'Chemical Information', icon: FlaskConical, description: 'MSDS/SDS, reactivity data, flammability limits' },
    { id: 'pfd', name: 'Process Flow Diagrams', icon: FileText, description: 'PFDs and process flows' },
    { id: 'pid', name: 'P&IDs / Equipment Hierarchy', icon: FolderTree, description: 'Piping & Instrumentation Diagrams' },
    { id: 'design', name: 'Design Basis & Engineering', icon: Book, description: 'Design basis and engineering documents' },
    { id: 'operating', name: 'Operating Envelopes', icon: Settings, description: 'Safe operating limits and envelopes' },
    { id: 'equipment', name: 'Equipment Data Sheets', icon: Database, description: 'Equipment specifications and data' },
    { id: 'process-desc', name: 'Process Description', icon: AlertTriangle, description: 'Operating intent and procedures' }
  ];

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'chemical-info':
        return <ChemicalInformation searchQuery={searchQuery} />;
      case 'pfd':
        return <ProcessFlowDiagrams />;
      case 'pid':
        return <PIDsEquipmentHierarchy />;
      case 'design':
        return <DesignBasisDocuments />;
      case 'operating':
        return <OperatingEnvelopes />;
      case 'equipment':
        return <EquipmentDataSheets />;
      case 'process-desc':
        return <ProcessDescription />;
      default:
        return <ChemicalInformation searchQuery={searchQuery} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Process Safety Information Repository</h1>
              <p className="text-sm text-gray-600 mt-1">Centralized repository for all process safety documentation</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Icon name="Home" size={16} />
              Return to Dashboard
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search chemicals, documents, equipment..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="px-6 overflow-x-auto">
          <div className="flex space-x-1 min-w-max">
            {sections?.map((section) => {
              const SectionIcon = section?.icon;
              return (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`flex items-center space-x-2 px-4 py-3 border-b-2 transition-colors ${
                    activeSection === section?.id
                      ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <SectionIcon className="w-4 h-4" />
                  <span className="font-medium text-sm whitespace-nowrap">{section?.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="p-6">
        {renderActiveSection()}
      </div>
    </div>
  );
};

export default ProcessSafetyInformationRepository;